import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useTeam } from '../../../context/TeamContext';
import { collection, query, where, getDocs, doc, getDoc, updateDoc, serverTimestamp, onSnapshot } from 'firebase/firestore';
import { db } from '../../../firebase/firebaseConfig';
import { SHIFT_TYPES } from '../../../data/nurseAppModels';

const PlanSwapRequestsPage = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { currentTeam } = useTeam();
  const [swapRequests, setSwapRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [responseType, setResponseType] = useState(null); // 'approve' or 'reject'
  const [rejectionReason, setRejectionReason] = useState('');

  // Fetch pending swap requests for current user
  useEffect(() => {
    if (!currentTeam || !currentUser) return;

    // Query only by teamId to avoid index requirement
    // Filter by requestedTo and status in memory
    const unsubscribe = onSnapshot(
      query(
        collection(db, 'shiftSwaps'),
        where('teamId', '==', currentTeam.id)
      ),
      async (snapshot) => {
        try {
          setLoading(true);
          console.log('[PlanSwapRequestsPage] Total swaps fetched:', snapshot.docs.length);
          
          const swaps = await Promise.all(
            snapshot.docs
              .filter(docSnap => {
                const data = docSnap.data();
                // Filter by requestedTo and PENDING status in memory
                const isForMe = data.requestedTo === currentUser.uid;
                const isPending = data.status === 'PENDING';
                console.log('[PlanSwapRequestsPage] Swap:', {
                  id: docSnap.id,
                  requestedTo: data.requestedTo,
                  currentUser: currentUser.uid,
                  status: data.status,
                  isForMe,
                  isPending,
                  planId: data.planId
                });
                return isForMe && isPending;
              })
              .map(async (docSnap) => {
                const data = docSnap.data();
                
                // Fetch user data for requestedBy
                const requestedByUser = await getDoc(doc(db, 'users', data.requestedBy));
                
                return {
                  id: docSnap.id,
                  ...data,
                  requestedByUser: requestedByUser.exists() ? requestedByUser.data() : null
                };
              })
          );
          
          console.log('[PlanSwapRequestsPage] Filtered swaps (PENDING for me):', swaps.length);
          setSwapRequests(swaps);
        } catch (err) {
          console.error('[PlanSwapRequestsPage] Error processing swap requests:', err);
        } finally {
          setLoading(false);
        }
      },
      (err) => {
        console.error('[PlanSwapRequestsPage] Error fetching swap requests:', err);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [currentTeam, currentUser]);

  // Handle approve/reject
  const handleResponse = async (requestId, type) => {
    setSelectedRequest(requestId);
    setResponseType(type);
    setRejectionReason('');
    setShowResponseModal(true);
  };

  // Submit response
  const submitResponse = async () => {
    if (!selectedRequest) return;

    if (responseType === 'reject' && !rejectionReason.trim()) {
      alert('กรุณากรอกเหตุผลในการปฏิเสธ');
      return;
    }

    setProcessing(true);
    try {
      const swapRef = doc(db, 'shiftSwaps', selectedRequest);
      const swapSnap = await getDoc(swapRef);
      
      if (!swapSnap.exists()) {
        throw new Error('ไม่พบคำขอแลกเวร');
      }

      const swapData = swapSnap.data();

      if (responseType === 'approve') {
        // Update swap status to MEMBER_APPROVED
        await updateDoc(swapRef, {
          status: 'MEMBER_APPROVED',
          memberApprovedAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });

        // TODO: Send notification to leader
        alert('อนุมัติคำขอแลกเวรเรียบร้อยแล้ว!');
      } else {
        // Update swap status to MEMBER_REJECTED
        await updateDoc(swapRef, {
          status: 'MEMBER_REJECTED',
          memberRejectedAt: serverTimestamp(),
          memberRejectionReason: rejectionReason.trim(),
          updatedAt: serverTimestamp()
        });

        // TODO: Send notification to requester
        alert('ปฏิเสธคำขอแลกเวรเรียบร้อยแล้ว!');
      }

      setShowResponseModal(false);
      setSelectedRequest(null);
      setResponseType(null);
      setRejectionReason('');
    } catch (err) {
      console.error('Error responding to swap request:', err);
      alert('เกิดข้อผิดพลาด: ' + err.message);
    } finally {
      setProcessing(false);
    }
  };

  // Get shift type label
  const getShiftTypeLabel = (type) => {
    switch (type) {
      case SHIFT_TYPES.MORNING: return 'เช้า';
      case SHIFT_TYPES.AFTERNOON: return 'บ่าย';
      case SHIFT_TYPES.NIGHT: return 'ดึก';
      default: return type;
    }
  };

  // Get shift type icon
  const getShiftTypeIcon = (type) => {
    switch (type) {
      case SHIFT_TYPES.MORNING: return '☀️';
      case SHIFT_TYPES.AFTERNOON: return '🌤️';
      case SHIFT_TYPES.NIGHT: return '🌙';
      default: return '';
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('th-TH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">กำลังโหลด...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate(-1)}
            className="text-blue-600 hover:text-blue-700 mb-4 flex items-center"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            กลับ
          </button>
          <h1 className="text-2xl font-bold text-gray-800">คำขอแลกเวร</h1>
          <p className="text-sm text-gray-600 mt-1">คำขอแลกเวรที่รอการยืนยัน</p>
        </div>

        {/* Swap Requests List */}
        {swapRequests.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
            <p className="text-gray-500">ไม่มีคำขอแลกเวรที่รอการยืนยัน</p>
          </div>
        ) : (
          <div className="space-y-4">
            {swapRequests.map((swap) => {
              const requesterName = swap.requestedByUser?.displayName || swap.requestedByName || 'Unknown';
              const requesterPhoto = swap.requestedByUser?.photoURL || null;

              return (
                <div key={swap.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <div className="flex items-start gap-4">
                    {/* Requester Avatar */}
                    <div className="flex-shrink-0">
                      {requesterPhoto ? (
                        <img
                          src={requesterPhoto}
                          alt={requesterName}
                          className="w-12 h-12 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-gray-300 flex items-center justify-center text-white font-semibold">
                          {requesterName?.charAt(0) || '?'}
                        </div>
                      )}
                    </div>

                    {/* Request Details */}
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-semibold text-gray-800">{requesterName}</h3>
                        <span className="text-xs px-2 py-1 bg-yellow-100 text-yellow-700 rounded">
                          รอการยืนยัน
                        </span>
                      </div>

                      {/* Swap Type */}
                      <div className="mb-3">
                        <p className="text-sm text-gray-600 mb-2">
                          {swap.swapType === 'SAME_DAY' && 'แลกเวรในวันเดียวกัน'}
                          {swap.swapType === 'DIFFERENT_DAY' && 'แลกเวรต่างวัน'}
                          {swap.swapType === 'COVER_REQUEST' && 'ขอให้ขึ้นเวรแทน'}
                        </p>

                        {/* Original Shift */}
                        <div className="p-3 bg-blue-50 rounded-lg mb-2">
                          <p className="text-xs text-gray-600 mb-1">เวรที่ต้องการแลก:</p>
                          <p className="text-sm font-medium text-gray-800">
                            {formatDate(swap.originalShift?.date)} - {getShiftTypeIcon(swap.originalShift?.shiftType)} {getShiftTypeLabel(swap.originalShift?.shiftType)}
                          </p>
                        </div>

                        {/* Requested Shift */}
                        {swap.requestedShift && (
                          <div className="p-3 bg-green-50 rounded-lg">
                            <p className="text-xs text-gray-600 mb-1">เวรที่ต้องการแลกมา:</p>
                            <p className="text-sm font-medium text-gray-800">
                              {formatDate(swap.requestedShift?.date)} - {getShiftTypeIcon(swap.requestedShift?.shiftType)} {getShiftTypeLabel(swap.requestedShift?.shiftType)}
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Message */}
                      {swap.message && (
                        <div className="mb-3 p-2 bg-gray-50 rounded text-sm text-gray-700">
                          <p className="font-medium mb-1">ข้อความ:</p>
                          <p>{swap.message}</p>
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleResponse(swap.id, 'approve')}
                          className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                        >
                          ยืนยัน
                        </button>
                        <button
                          onClick={() => handleResponse(swap.id, 'reject')}
                          className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
                        >
                          ปฏิเสธ
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Response Modal */}
        {showResponseModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                {responseType === 'approve' ? 'ยืนยันคำขอแลกเวร' : 'ปฏิเสธคำขอแลกเวร'}
              </h3>

              {responseType === 'reject' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    เหตุผลในการปฏิเสธ *
                  </label>
                  <textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    rows="3"
                    placeholder="กรุณากรอกเหตุผล..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    required
                  />
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowResponseModal(false);
                    setSelectedRequest(null);
                    setResponseType(null);
                    setRejectionReason('');
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
                >
                  ยกเลิก
                </button>
                <button
                  onClick={submitResponse}
                  disabled={processing || (responseType === 'reject' && !rejectionReason.trim())}
                  className={`flex-1 px-4 py-2 rounded-lg text-white transition disabled:opacity-50 disabled:cursor-not-allowed ${
                    responseType === 'approve'
                      ? 'bg-green-600 hover:bg-green-700'
                      : 'bg-red-600 hover:bg-red-700'
                  }`}
                >
                  {processing ? 'กำลังดำเนินการ...' : responseType === 'approve' ? 'ยืนยัน' : 'ปฏิเสธ'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlanSwapRequestsPage;


